import {
    Box,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack, TableBody,
    TableCell, TableHead, TableRow,
    Typography
} from "@mui/material";
import React from "react";
import {Table} from "react-bootstrap";

const PostbackGuide = () => {
    return <>
        <Box sx={{ p: 3 }}>
            <Typography variant="body1" paragraph>
                This documentation outlines how postbacks work in our affiliate integration system, including the use of custom tracking parameters <code>v</code> and <code>v2</code> in affiliate links.
            </Typography>

            <Typography variant="h6" gutterBottom fontWeight="bold">
                What is a Postback?
            </Typography>
            <Typography variant="body1" paragraph>
                A postback is a server-to-server notification mechanism that allows our system to inform your tracking system about specific events, such as user registrations. When an event occurs, our system sends an HTTP request to a predefined URL you provide, including relevant parameters about the event. This enables real-time tracking of conversions or other actions.
            </Typography>

            <Typography variant="h6" gutterBottom fontWeight="bold">
                Affiliate Link Flow (Standard)
            </Typography>
            <Typography variant="body1" paragraph>
                In the basic affiliate integration, the process works as follows:
            </Typography>
            <List>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 'unset', mr: 1 }}>•</ListItemIcon>
                    <ListItemText primary="Link Creation: We provide you with a unique affiliate link." />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 'unset', mr: 1 }}>•</ListItemIcon>
                    <ListItemText primary="Link Sharing: You share this link with your audience." />
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 'unset', mr: 1 }}>•</ListItemIcon>
                    <ListItemText primary="User Registration: When a person clicks the link and registers on our platform, the registration is attributed to you as the affiliate." />
                </ListItem>
            </List>

            <Divider sx={{ mt: 2, mb: 2}}/>

            {/* Using Custom Tracking Parameters (v and v2) */}
            <Typography variant="h6" gutterBottom fontWeight="bold">
                Using Custom Tracking Parameters (v and v2)
            </Typography>
            <Typography variant="body1" paragraph>
                Some affiliates use additional tracking systems to monitor link interactions more granularly, requiring unique values to be passed with each link click.
            </Typography>
            <Typography>
                Our system supports this through the <code>v</code> and <code>v2</code> parameters in affiliate links.
            </Typography>

            <Divider sx={{ mt: 2, mb: 2}}/>

            {/* Enhanced Flow with Custom Tracking */}
            <Typography variant="h6" gutterBottom fontWeight="bold">
                Enhanced Flow with Custom Tracking
            </Typography>
            <Typography variant="body1" paragraph>
                When using extra tracking systems, the flow adjusts as follows:
            </Typography>
            <List>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 'unset', mr: 1 }}>1</ListItemIcon>
                    Link Creation by Affiliate: Your system generates a new tracking link:
                </ListItem>
                <Typography sx={{ mt: 1, textAlign: 'center' }}>
                    <code>https://yourtracking.com/click?campaign=example</code>
                </Typography>
                <ListItem>
                <ListItemIcon sx={{ minWidth: 'unset', mr: 1 }}>2</ListItemIcon>
                    User Click: A person clicks on your tracking link.
                </ListItem>
                <ListItem>
                    <ListItemIcon sx={{ minWidth: 'unset', mr: 1 }}>3</ListItemIcon>
                    <ListItemText>
                        Redirection with Parameters: Your system redirects the user to our affiliate link, appending unique values in the <code>v</code> and <code>v2</code> URL parameters
                    </ListItemText>
                </ListItem>
                <Typography sx={{ mt: 1, textAlign: 'center' }}>
                    <code>https://vegasaffiliate.com/links/redirect/513fffd3-e1f3-4dc8-bb64-7744bd3c9ac6?v=meta&v2=click123</code>
                </Typography>
                <ListItem>
                    <Typography>
                        Upon registration, our system records the user's information along with the <code>v</code> and <code>v2</code> values.
                    </Typography>
                </ListItem>

                <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                    Example of a player registration using link from step 3:
                </Typography>
                <Paper sx={{p: 2, mb: 2}}>
                    <Table sx={{ mt: 2, minWidth: 400 }} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>ID</strong></TableCell>
                                <TableCell><strong>Email</strong></TableCell>
                                <TableCell><strong>v</strong></TableCell>
                                <TableCell><strong>v2</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            <TableRow>
                                <TableCell>1</TableCell>
                                <TableCell>john@gmail.com</TableCell>
                                <TableCell>meta</TableCell>
                                <TableCell>click123</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </Paper>
                <Typography variant="body1" paragraph>
                    These parameters are optional; if not provided, the registration is still linked to you via the standard affiliate ID, but no custom tracking values are recorded.
                </Typography>
            </List>

            <Divider sx={{ mt: 2, mb: 2}}/>

            {/* Postback Mechanism */}
            <Typography variant="h6" gutterBottom fontWeight="bold">
                Postback Mechanism
            </Typography>
            <Typography variant="body1" paragraph>
                When a user registers through an affiliate link, our system triggers a postback request to the URL you have specified. This request includes key parameters to help you identify the event and correlate it with your tracking data.
            </Typography>
            <Typography variant="body1" paragraph>
                If <code>v</code> and <code>v2</code> parameters were included in the affiliate link, they can be used in Postback requests.
            </Typography>
            <Typography variant="body1" paragraph>
                To use <code>v</code> and <code>v2</code> parameters in postback urls, just
                specify <code>{'{v}'}</code> and <code>{'{v2}'}</code> placeholders in url like below
            </Typography>
            <Typography sx={{ mt: 1, textAlign: 'center' }}>
                <code>https://yourpostback.com/postback?subid={'{v}'}&cid={'{v2}'}</code>
            </Typography>
            <Typography sx={{ mt: 1 }}>
                When Postback event is fired it will automatically replace placeholders with real values, see example below
            </Typography>
            <Typography sx={{ mt: 1, textAlign: 'center' }}>
                <code>https://yourpostback.com/postback?subid=meta&cid=click123</code>
            </Typography>
        </Box>
    </>
}

export default PostbackGuide;
